import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import NotesIcon from '@mui/icons-material/Notes';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../../services/api';
import { t } from '../../../utils/textUtils';

// Helper function to get full URL for file paths
const getFullFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  
  // If it's already a full URL, return it
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  
  // If it's a relative path starting with /api/uploads, convert to local backend URL
  if (fileUrl.startsWith('/api/')) {
    return `http://localhost:3000${fileUrl}`;
  }
  
  // If it's just a filename or path without protocol, assume it's in the backend uploads folder
  return `http://localhost:3000/uploads/${fileUrl.replace(/^\/+/, '')}`;
};

const ProjectFieldProgress = ({ projectId, projectData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressSubmissions, setProgressSubmissions] = useState([]);
  const [allProgressSubmissions, setAllProgressSubmissions] = useState([]);
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    submission: null
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'approved'
  
  // Fetch ALL progress submissions (without pagination from server)
  useEffect(() => {
    const fetchProgressSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching progress submissions for projectId:', projectId);
        // Fetch all data without pagination to handle filtering on frontend
        const response = await api.get(`/progress-submissions?projectId=${projectId}&limit=1000`);
        
        console.log('API Response:', response.data);
        console.log('Progress submissions:', response.data?.progressSubmissions);
        
        if (response.data && response.data.progressSubmissions) {
          setAllProgressSubmissions(response.data.progressSubmissions);
          console.log('Set all progress submissions:', response.data.progressSubmissions.length, 'items');
        } else {
          setAllProgressSubmissions([]);
          console.log('No progress submissions found in response');
        }
      } catch (err) {
        console.error('Error fetching progress submissions:', err);
        console.error('Error response:', err.response?.data);
        setError(`Failed to load progress submissions: ${err.response?.data?.message || err.message}`);
        setAllProgressSubmissions([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (projectId) {
      fetchProgressSubmissions();
    }
  }, [projectId]); // Remove page and limit dependencies

  // Filter submissions based on status and update pagination
  useEffect(() => {
    if (allProgressSubmissions.length === 0) {
      setProgressSubmissions([]);
      setTotalPages(1);
      return;
    }

    let filtered = [];
    if (statusFilter === 'all') {
      filtered = [...allProgressSubmissions];
    } else {
      filtered = allProgressSubmissions.filter(submission => 
        submission.status === statusFilter
      );
    }
    
    // Calculate pagination for filtered results
    const totalFiltered = filtered.length;
    const newTotalPages = Math.ceil(totalFiltered / limit) || 1;
    
    // Ensure current page is valid
    let currentPage = page;
    if (currentPage > newTotalPages && newTotalPages > 0) {
      currentPage = 1;
      setPage(1);
    }
    
    // Get current page data
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filtered.slice(startIndex, endIndex);
    
    console.log('Filter Debug:', {
      statusFilter,
      totalData: allProgressSubmissions.length,
      filteredCount: filtered.length,
      currentPage,
      newTotalPages,
      startIndex,
      endIndex,
      paginatedCount: paginatedData.length
    });
    
    setProgressSubmissions(paginatedData);
    setTotalPages(newTotalPages);
  }, [statusFilter, allProgressSubmissions, page, limit]);
  
  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (err) {
      return 'Invalid date';
    }
  };
  
  // Get status chip for progress submissions
  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="DISETUJUI"
            color="success"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'draft':
        return (
          <Chip
            icon={<PendingIcon />}
            label="DRAFT"
            color="info"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<CancelIcon />}
            label="DITOLAK"
            color="error"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'pending':
      default:
        return (
          <Chip
            icon={<PendingIcon />}
            label="MENUNGGU"
            color="warning"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
    }
  };
  
  // Get user role icon
  const getUserRoleIcon = (role) => {
    if (role === 'site_manager' || role === 'Site Manager') {
      return <SupervisorAccountIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />;
    } else {
      return <EngineeringIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />;
    }
  };
  
  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon fontSize="small" />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon fontSize="small" />;
    } else {
      return <InsertDriveFileIcon fontSize="small" />;
    }
  };
  
  // Handle view submission details
  const handleViewSubmission = async (submission) => {
    try {
      // Fetch detailed submission data including attachments
      const response = await api.get(`/progress-submissions/${submission.id}`);
      
      if (response.data) {
        setDetailDialog({
          open: true,
          submission: {
            ...submission,
            ...response.data,
            attachments: response.data.attachments || []
          }
        });
      }
    } catch (err) {
      console.error('Error fetching submission details:', err);
    }
  };
  
  // Close detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialog({
      open: false,
      submission: null
    });
  };
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event, newValue) => {
    const filterMap = {
      0: 'all',
      1: 'pending', 
      2: 'approved'
    };
    setStatusFilter(filterMap[newValue]);
    setPage(1); // Always reset to page 1 when filter changes
  };

  // Get counts for each status
  const getStatusCounts = () => {
    const counts = {
      all: allProgressSubmissions.length,
      pending: allProgressSubmissions.filter(s => s.status === 'pending').length,
      approved: allProgressSubmissions.filter(s => s.status === 'approved').length
    };
    return counts;
  };
  
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>
          {t('PENGAJUAN PROGRES')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#8D6E63' }}>
          {t('LACAK PENGAJUAN PROGRES DAN STATUS PERSETUJUAN UNTUK')} {projectData?.name}
        </Typography>
      </Box>

      {/* Status Filter Tabs */}
      {allProgressSubmissions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ backgroundColor: '#FFFFFF', borderRadius: 1 }}>
            <Tabs 
              value={statusFilter === 'all' ? 0 : statusFilter === 'pending' ? 1 : 2}
              onChange={handleStatusFilterChange}
              sx={{ 
                '& .MuiTab-root': { 
                  textTransform: 'none',
                  fontWeight: 'bold',
                  color: '#5D4037'
                },
                '& .Mui-selected': {
                  color: '#F0C14B !important'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#F0C14B'
                }
              }}
            >
              <Tab 
                label={`SEMUA (${getStatusCounts().all})`}
                sx={{ minWidth: 120 }}
              />
              <Tab 
                label={`MENUNGGU (${getStatusCounts().pending})`}
                sx={{ minWidth: 120 }}
              />
              <Tab 
                label={`DISETUJUI (${getStatusCounts().approved})`}
                sx={{ minWidth: 120 }}
              />
            </Tabs>
          </Paper>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Debug Info - Remove in production */}
      {/* {process.env.NODE_ENV === 'development' && allProgressSubmissions.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Debug: Total={allProgressSubmissions.length}, 
          Showing={progressSubmissions.length} items on page {page}/{totalPages}, 
          Filter={statusFilter}, 
          Counts: {JSON.stringify(getStatusCounts())}
        </Alert>
      )} */}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#F0C14B' }} />
        </Box>
      ) : progressSubmissions.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('TIDAK ADA PENGAJUAN PROGRES UNTUK PROYEK INI')}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            mb: 3
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9F5EB' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('PROYEK / TUGAS')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('DIKIRIM OLEH')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('TANGGAL')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('VOLUME')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('STATUS')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('AKSI')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {progressSubmissions.map((submission, index) => (
                  <TableRow key={submission.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {projectData?.name || t('PROYEK TIDAK DIKETAHUI')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {submission.taskName || t('TUGAS TIDAK DIKETAHUI')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {submission.workCategoryName || t('TIDAK DITENTUKAN')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getUserRoleIcon(submission.submitterRole || 'site_engineer')}
                        <Box>
                          <Typography variant="body2" sx={{ color: '#1976d2' }}>
                            {submission.submitterRole === 'site_manager'
                              ? t('MANAJER LAPANGAN')
                              : t('INSINYUR LAPANGAN')}
                          </Typography>
                          <Typography variant="body2">
                            {submission.submitterName || submission.submitterUsername || 'Unknown'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(submission.createdAt)}</TableCell>
                    <TableCell>{submission.volume} {submission.unit || ''}</TableCell>
                    <TableCell>{getStatusChip(submission.status)}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewSubmission(submission)}
                        sx={{ color: '#5D4037' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
          
          {/* Detail Dialog */}
          <Dialog 
            open={detailDialog.open} 
            onClose={handleCloseDetailDialog}
            maxWidth="md"
            fullWidth
          >
            {detailDialog.submission && (
              <>
                <DialogTitle>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('DETAIL PENGAJUAN PROGRES')}
                  </Typography>
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getUserRoleIcon(detailDialog.submission.submitterRole || 'site_engineer')}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Submitted by {detailDialog.submission.submitterName || detailDialog.submission.submitterUsername || 'Unknown'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666', ml: 4 }}>
                        {formatDate(detailDialog.submission.createdAt)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Project & Task Details
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Project:</strong> {detailDialog.submission.projectName || 'Unknown Project'}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Task:</strong> {detailDialog.submission.taskName || 'Unknown Task'}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Work Category:</strong> {detailDialog.submission.workCategoryName || 'N/A'}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Modular Unit:</strong> {detailDialog.submission.modularUnitName || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Progress Details
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SquareFootIcon sx={{ mr: 1, color: '#757575', fontSize: 20 }} />
                        <Typography variant="body2">
                          <strong>Total Volume:</strong> {detailDialog.submission.taskQuantity || 0} {detailDialog.submission.unit || 'm³'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SquareFootIcon sx={{ mr: 1, color: '#757575', fontSize: 20 }} />
                        <Typography variant="body2">
                          <strong>Volume Selesai Saat Ini:</strong> {detailDialog.submission.completedQuantity || 0} {detailDialog.submission.unit || 'm³'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SquareFootIcon sx={{ mr: 1, color: '#757575', fontSize: 20 }} />
                        <Typography variant="body2">
                          <strong>Volume Pengajuan:</strong> {detailDialog.submission.volume || 0} {detailDialog.submission.unit || 'm³'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SquareFootIcon sx={{ mr: 1, color: '#757575', fontSize: 20 }} />
                        <Typography variant="body2">
                          <strong>Progress Saat Ini:</strong> {((detailDialog.submission.completedQuantity || 0) / (detailDialog.submission.taskQuantity || 1) * 100).toFixed(2)}%
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SquareFootIcon sx={{ mr: 1, color: '#757575', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          <strong>Progress Setelah Disetujui:</strong> {(((detailDialog.submission.completedQuantity || 0) + parseFloat(detailDialog.submission.volume || 0)) / (detailDialog.submission.taskQuantity || 1) * 100).toFixed(2)}%
                        </Typography>
                      </Box>
                    </Grid>
                    
                    {detailDialog.submission.notes && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                          <NotesIcon sx={{ mr: 1, color: '#757575', fontSize: 20, mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Notes:
                            </Typography>
                            <Typography variant="body2">
                              {detailDialog.submission.notes}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    
                    {detailDialog.submission.attachments && detailDialog.submission.attachments.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 1 }}>
                          Attachments
                        </Typography>
                        
                        <Grid container spacing={1}>
                          {detailDialog.submission.attachments.map((attachment, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Paper 
                                elevation={1} 
                                sx={{ 
                                  p: 1, 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                              >
                                {attachment.fileType?.startsWith('image/') ? (
                                  <Box sx={{ mb: 1, width: '100%', height: '120px', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <a href={getFullFileUrl(attachment.fileUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%' }}>
                                      <img 
                                        src={getFullFileUrl(attachment.fileUrl)} 
                                        alt={attachment.fileName} 
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = 'https://via.placeholder.com/100x100?text=Image+Error';
                                        }}
                                      />
                                    </a>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    {getFileIcon(attachment.fileType)}
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                      Preview not available
                                    </Typography>
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ overflow: 'hidden' }}>
                                    <Typography variant="body2" noWrap title={attachment.fileName}>
                                      {attachment.fileName}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {(attachment.fileSize / 1024).toFixed(1)} KB
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <IconButton 
                                      size="small"
                                      href={getFullFileUrl(attachment.fileUrl)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="View"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                      size="small"
                                      href={getFullFileUrl(attachment.fileUrl)}
                                      download={attachment.fileName}
                                      title="Download"
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    )}
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          <strong>Status:</strong>
                        </Typography>
                        {getStatusChip(detailDialog.submission.status)}
                      </Box>
                      
                      {detailDialog.submission.status === 'approved' && (
                        <Box sx={{ mt: 1, ml: 4 }}>
                          <Typography variant="body2">
                            <strong>Approved by:</strong> {detailDialog.submission.reviewerName || 'Unknown'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Approved on:</strong> {formatDate(detailDialog.submission.reviewedAt)}
                          </Typography>
                        </Box>
                      )}
                      
                      {(detailDialog.submission.status === 'rejected' || detailDialog.submission.status === 'draft') && (
                        <Box sx={{ mt: 1, ml: 4 }}>
                          <Typography variant="body2">
                            <strong>Rejected by:</strong> {detailDialog.submission.reviewerName || 'Unknown'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Rejected on:</strong> {formatDate(detailDialog.submission.reviewedAt)}
                          </Typography>
                          {detailDialog.submission.rejectionReason && (
                            <Typography variant="body2">
                              <strong>Reason:</strong> {detailDialog.submission.rejectionReason}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDetailDialog} variant="outlined">
                    {t('TUTUP')}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default ProjectFieldProgress;
